import { useCallback, useRef, useState, useEffect } from 'react';
import { useViewportStore } from '../../stores/viewportStore';
import { useCanvasStore } from '../../stores/canvasStore';
import { UNIT_CONVERSIONS } from '../../lib/artboardPresets';

export const RULER_SIZE = 24;

interface RulerOverlayProps {
  artboardLeft: number;
  artboardTop: number;
  containerWidth: number;
  containerHeight: number;
  onGuideCreate: (orientation: 'horizontal' | 'vertical', position: number) => void;
}

function calculateTickInterval(zoom: number, unit: string): { major: number; minor: number; labelEvery: number } {
  const screenSpacingTarget = 100;
  const rawInterval = screenSpacingTarget / zoom;

  if (unit === 'px') {
    const niceIntervals = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000];
    const major = niceIntervals.find((n) => n >= rawInterval) ?? 5000;
    const minor = major <= 10 ? 1 : major <= 50 ? 5 : major <= 100 ? 10 : major <= 500 ? 50 : major <= 1000 ? 100 : 500;
    return { major, minor, labelEvery: major };
  }

  const conv = UNIT_CONVERSIONS[unit];
  const rawInUnit = rawInterval * conv.factor;
  const niceUnits = [0.1, 0.25, 0.5, 1, 2, 5, 10, 25, 50, 100];
  const majorInUnit = niceUnits.find((n) => n >= rawInUnit) ?? 100;
  const major = majorInUnit / conv.factor;
  const minor = major / (majorInUnit >= 1 ? 4 : 2);
  return { major, minor, labelEvery: major };
}

function formatLabel(positionPx: number, unit: string): string {
  const conv = UNIT_CONVERSIONS[unit];
  const value = positionPx * conv.factor;
  return value.toFixed(conv.decimals);
}

function drawRuler(
  ctx: CanvasRenderingContext2D,
  orientation: 'horizontal' | 'vertical',
  width: number,
  height: number,
  artboardOffset: number,
  zoom: number,
  unit: string,
  artboardSize: number,
) {
  const dpr = window.devicePixelRatio || 1;
  const length = orientation === 'horizontal' ? width : height;
  const thickness = orientation === 'horizontal' ? height : width;

  ctx.clearRect(0, 0, width * dpr, height * dpr);
  ctx.scale(dpr, dpr);

  ctx.fillStyle = '#111111';
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.lineWidth = 1;
  if (orientation === 'horizontal') {
    ctx.beginPath();
    ctx.moveTo(0, thickness - 0.5);
    ctx.lineTo(width, thickness - 0.5);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.moveTo(thickness - 0.5, 0);
    ctx.lineTo(thickness - 0.5, height);
    ctx.stroke();
  }

  const { major, minor, labelEvery } = calculateTickInterval(zoom, unit);

  const startArtboard = -artboardOffset / zoom;
  const endArtboard = (length - artboardOffset) / zoom;

  const firstTick = Math.floor(startArtboard / minor) * minor;
  const lastTick = Math.ceil(endArtboard / minor) * minor;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = '9px Inter, system-ui, sans-serif';

  for (let pos = firstTick; pos <= lastTick; pos += minor) {
    const screenPos = artboardOffset + pos * zoom;
    if (screenPos < 0 || screenPos > length) continue;

    const isMajor = Math.abs(pos % major) < 0.001;
    const isLabel = Math.abs(pos % labelEvery) < 0.001;
    const inArtboard = pos >= 0 && pos <= artboardSize;
    const tickLen = isMajor ? thickness * 0.6 : thickness * 0.3;

    ctx.strokeStyle = inArtboard
      ? isMajor ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.15)'
      : isMajor ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.06)';
    ctx.lineWidth = 1;

    ctx.beginPath();
    if (orientation === 'horizontal') {
      const x = Math.round(screenPos) + 0.5;
      ctx.moveTo(x, thickness - tickLen);
      ctx.lineTo(x, thickness);
    } else {
      const y = Math.round(screenPos) + 0.5;
      ctx.moveTo(thickness - tickLen, y);
      ctx.lineTo(thickness, y);
    }
    ctx.stroke();

    if (isLabel && pos !== firstTick) {
      ctx.fillStyle = inArtboard ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.2)';
      const label = formatLabel(pos, unit);

      if (orientation === 'horizontal') {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(label, screenPos, 2);
      } else {
        ctx.save();
        ctx.translate(2, screenPos);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }
    }
  }

  const startScreen = artboardOffset;
  const endScreen = artboardOffset + artboardSize * zoom;

  ctx.strokeStyle = 'rgba(6, 182, 212, 0.5)';
  ctx.lineWidth = 1.5;

  if (orientation === 'horizontal') {
    if (startScreen >= 0 && startScreen <= width) {
      ctx.beginPath();
      ctx.moveTo(startScreen, 0);
      ctx.lineTo(startScreen, thickness);
      ctx.stroke();
    }
    if (endScreen >= 0 && endScreen <= width) {
      ctx.beginPath();
      ctx.moveTo(endScreen, 0);
      ctx.lineTo(endScreen, thickness);
      ctx.stroke();
    }
  } else {
    if (startScreen >= 0 && startScreen <= height) {
      ctx.beginPath();
      ctx.moveTo(0, startScreen);
      ctx.lineTo(thickness, startScreen);
      ctx.stroke();
    }
    if (endScreen >= 0 && endScreen <= height) {
      ctx.beginPath();
      ctx.moveTo(0, endScreen);
      ctx.lineTo(thickness, endScreen);
      ctx.stroke();
    }
  }

  ctx.setTransform(1, 0, 0, 1, 0, 0);
}

export default function RulerOverlay({
  artboardLeft,
  artboardTop,
  containerWidth,
  containerHeight,
  onGuideCreate,
}: RulerOverlayProps) {
  const { zoom, rulerUnit } = useViewportStore();
  const { canvasWidth: artboardWidth, canvasHeight: artboardHeight, showRulers } = useCanvasStore();
  const hCanvasRef = useRef<HTMLCanvasElement>(null);
  const vCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState<'horizontal' | 'vertical' | null>(null);
  const [dragPos, setDragPos] = useState<number>(0);
  const containerElRef = useRef<HTMLElement | null>(null);

  const drawRulers = useCallback(() => {
    const dpr = window.devicePixelRatio || 1;

    const hCanvas = hCanvasRef.current;
    if (hCanvas) {
      const hWidth = containerWidth - RULER_SIZE;
      hCanvas.width = hWidth * dpr;
      hCanvas.height = RULER_SIZE * dpr;
      const hCtx = hCanvas.getContext('2d');
      if (hCtx) {
        drawRuler(hCtx, 'horizontal', hWidth, RULER_SIZE, artboardLeft - RULER_SIZE, zoom, rulerUnit, artboardWidth);
      }
    }

    const vCanvas = vCanvasRef.current;
    if (vCanvas) {
      const vHeight = containerHeight - RULER_SIZE;
      vCanvas.width = RULER_SIZE * dpr;
      vCanvas.height = vHeight * dpr;
      const vCtx = vCanvas.getContext('2d');
      if (vCtx) {
        drawRuler(vCtx, 'vertical', RULER_SIZE, vHeight, artboardTop - RULER_SIZE, zoom, rulerUnit, artboardHeight);
      }
    }
  }, [containerWidth, containerHeight, artboardLeft, artboardTop, zoom, rulerUnit, artboardWidth, artboardHeight]);

  useEffect(() => {
    drawRulers();
  }, [drawRulers]);

  const handleMouseDown = useCallback(
    (orientation: 'horizontal' | 'vertical') => (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(orientation);
      setDragPos(orientation === 'horizontal' ? e.clientY : e.clientX);
      containerElRef.current = (e.currentTarget as HTMLElement).parentElement;
    },
    [],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      setDragPos(isDragging === 'horizontal' ? e.clientY : e.clientX);
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const container = containerElRef.current;
      if (!container) {
        setIsDragging(null);
        return;
      }
      const containerRect = container.getBoundingClientRect();

      if (isDragging === 'horizontal') {
        const relativeY = e.clientY - containerRect.top - RULER_SIZE;
        const artboardY = relativeY / zoom + (RULER_SIZE - artboardTop) / zoom;
        if (artboardY > -500 && artboardY < artboardHeight + 500) {
          onGuideCreate('horizontal', Math.round(artboardY));
        }
      } else {
        const relativeX = e.clientX - containerRect.left - RULER_SIZE;
        const artboardX = relativeX / zoom + (RULER_SIZE - artboardLeft) / zoom;
        if (artboardX > -500 && artboardX < artboardWidth + 500) {
          onGuideCreate('vertical', Math.round(artboardX));
        }
      }

      setIsDragging(null);
    },
    [isDragging, artboardLeft, artboardTop, zoom, artboardWidth, artboardHeight, onGuideCreate],
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isDragging === 'horizontal' ? 'row-resize' : 'col-resize';
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!showRulers) return null;

  return (
    <>
      <div
        className="absolute top-0 left-0 z-30 bg-[#111111] border-r border-b border-white/[0.08] flex items-center justify-center"
        style={{ width: RULER_SIZE, height: RULER_SIZE }}
      >
        <span className="text-[8px] text-white/30 font-medium select-none">{rulerUnit}</span>
      </div>

      <canvas
        ref={hCanvasRef}
        className="absolute top-0 z-20 cursor-s-resize"
        style={{
          left: RULER_SIZE,
          width: containerWidth - RULER_SIZE,
          height: RULER_SIZE,
        }}
        onMouseDown={handleMouseDown('horizontal')}
        title="Drag down to create horizontal guide"
      />

      <canvas
        ref={vCanvasRef}
        className="absolute left-0 z-20 cursor-e-resize"
        style={{
          top: RULER_SIZE,
          width: RULER_SIZE,
          height: containerHeight - RULER_SIZE,
        }}
        onMouseDown={handleMouseDown('vertical')}
        title="Drag right to create vertical guide"
      />

      {isDragging && (
        <div
          className="fixed z-50 pointer-events-none"
          style={
            isDragging === 'horizontal'
              ? {
                  left: 0,
                  right: 0,
                  top: dragPos,
                  height: 1,
                  background: 'rgba(34, 211, 238, 0.8)',
                  boxShadow: '0 0 6px rgba(34, 211, 238, 0.4)',
                }
              : {
                  top: 0,
                  bottom: 0,
                  left: dragPos,
                  width: 1,
                  background: 'rgba(167, 139, 250, 0.8)',
                  boxShadow: '0 0 6px rgba(167, 139, 250, 0.4)',
                }
          }
        />
      )}
    </>
  );
}
